import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';

const defaultPagination = {
  page: 1,
  rowsPerPage: 50,
  sortBy: 'timestamp',
  descending: true,
  total: null,
};

export const state = {
  search: null,
  vaultTransactions: [],
  statusCount: {
    error: 0,
    pending: 0,
    processing: 0,
    queued: 0,
  },
  fromUserWallets: [],
  transactionCreated: null,
  errorOntransactionCreated: null,
  errorOnRequeue: null,
  errorOnUpdate: null,
  toUserWallets: [],
  vaultTransactionsPagination: defaultPagination,
  vaultTransactionsNextPage: [],
  loading: false,
};


export const mutations = {
  setVaultTransactions(context, vaultTransactions) {
    context.vaultTransactions = vaultTransactions;
    context.loading = false;
  },

  setFromUserWallets(context, wallets) {
    context.fromUserWallets = wallets;
    context.loading = false;
  },
  setTransactionCreated(context, transaction) {
    context.transactionCreated = transaction;
    context.loading = false;
  },
  setErrorOnRequeue(context, error) {
    context.errorOnRequeue = error;
    context.loading = false;
  },
  setErrorOnUpdate(context, error) {
    context.errorOnUpdate = error;
    context.loading = false;
  },
  setErrorOnTransactionCreated(context, error) {
    context.errorOntransactionCreated = error;
    context.loading = false;
  },
  setSearch(context, search) {
    context.search = search;
  },
  setToUserWallets(context, wallets) {
    context.toUserWallets = wallets;
    context.loading = false;
  },

  reset(context) {
    context.vaultTransactions = [];
    context.vaultTransactionsNextPage = [];
    context.fromUserWallets = [];
    context.toUserWallets = [];
    context.vaultTransactionsPagination = defaultPagination;
    context.loading = false;
    context.search = null;
  },

  setVaultTransactionsBackPage(context) {
    context.vaultTransactionsNextPage.pop();
    context.vaultTransactionsNextPage.pop();
    context.loading = false;
  },

  setVaultTransactionsPagination(context, pagination) {
    context.vaultTransactionsPagination = pagination;
  },

  setLimitItemsPerPage(context, value) {
    context.vaultTransactionsPagination.limit = value;
  },
  setTotalItems(context, value) {
    context.vaultTransactionsPagination.total = value;
  },

  setStatusCount(context, { status, value }) {
    context.statusCount[status] = value;
  },

  setLoading(context, value) {
    context.loading = value;
  },
};

export const getters = {
  getVaultTransactionsPagination: currentState => currentState.vaultTransactionsPagination,
  statusCount: currentState => currentState.statusCount,
  fromUserVaultWallets: currentState => currentState.fromUserWallets,
  toUserVaultWallets: currentState => currentState.toUserWallets,
  vaultErrorOntransactionCreated: currentState => currentState.errorOntransactionCreated,
  errorOnRequeue: currentState => currentState.errorOnRequeue,
};

export const actions = {
  async setLimitItemsPerPage({ commit, dispatch }, limit) {
    commit('setLimitItemsPerPage', limit);
    commit('clearVaultTransactionsNextPage');
  },

  async reset({ commit }) {
    commit('reset');
  },

  async getUserWallets({ rootState }, userId) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };

    return axios.get(`${window.BASE_API.VAULT}/users/${userId}/wallets`, options);
  },

  async createVaultTransaction({ commit, dispatch, rootState }, payload) {
    commit('setErrorOnTransactionCreated', null);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const { amount, toWallet, extIdentifier, fromUserId, fromWallet, token } = payload;
    const url = `${window.BASE_API.VAULT}/users/${fromUserId}/wallets/${fromWallet}/tokens/${token}/transactions`;
    try {
      commit('setLoading', true);
      const createdTransaction = await axios.post(url, {
        amount: Number(amount),
        toWallet,
        extIdentifier,
      }, options);
      commit('setTransactionCreated', createdTransaction.data);
      commit('setLoading', false);
    } catch (error) {
      console.error(error);
      commit('setLoading', false);
      commit('setErrorOnTransactionCreated', error.message);
      throw error;
    }
  },
  async requeueVaultTransactions({ commit, dispatch, rootState }, payload) {
    commit('setLoading', true);
    commit('setErrorOnRequeue', null);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const url = `${window.BASE_API.VAULT}/admin/transactions/requeue`;

    return axios.post(url, {
      transactions: payload,
    }, options)
      .then(data => data)
      .catch((error) => {
        commit('setErrorOnRequeue', error.response.data);

        return Promise.reject(error.response.data);
      }).finally(() => {
        commit('setLoading', false);
      });
  },

  async startProcessingQueuedTransactions({ commit, dispatch, rootState }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const url = `${window.BASE_API.VAULT}/admin/transactionStartProcessingQueued`;
    try {
      commit('setLoading', true);
      await axios.get(url, options);

      commit('setLoading', false);
    } catch (error) {
      console.error(error.message);
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async updateVaultTransaction({ commit, dispatch, rootState }, payload) {
    commit('setErrorOnUpdate', null);
    commit('setLoading', true);
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    const url = `${window.BASE_API.VAULT}/admin/transactions/${payload.extIdentifier}/update`;

    try {
      await axios.put(url, { ...payload }, options);
      await new Promise(resolve => setTimeout(resolve, 3000));

      commit('setLoading', false);
    } catch (error) {
      console.error(error.message);
      commit('setLoading', false);
      commit('setErrorOnUpdate', error.message);
      throw error;
    }
  },

  async getVaultTransactions({ commit, rootState, ...context }, { search, filters } = { search: '', filters: {} }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    try {
      commit('setLoading', true);
      if (search) {
        commit('setSearch', search);
      }
      const searchState = context.state.search || search;
      filters = filters || {};

      const filter = [
        {
          term: { type: 'payin' },
        },
        ...(!!filters.status &&
          [{
            term: { status: filters.status },
          }]),
        ...(!!filters.token &&
          [{
            term: { token: filters.token.toLowerCase() },
          }]),
      ];

      const { page, rowsPerPage, sortBy, descending } = context.state.vaultTransactionsPagination;
      const url = `${window.BASE_API.VAULT}/admin/transactions`;
      const query = {
        from: (page - 1) * rowsPerPage,
        size: rowsPerPage,
        query: {
          bool: {
            ...(searchState && {
              must: {
                multi_match: {
                  query: searchState,
                  operator: 'and',
                },
              },
            }),
            filter,
          },
        },
        sort: {
          [sortBy || 'timestamp']: {
            order: descending ? 'desc' : 'asc',
          },
        },
      };
      const response = await axios.post(url, query, options);
      commit('setTotalItems', _.get(response, 'data.body.hits.total.value'));
      const vaultTransactions = serializeTransactions(response);
      commit('setVaultTransactions', vaultTransactions);
      commit('setLoading', false);
    } catch (err) {
      console.error(err);
      commit('setLoading', false);
    }
  },

  async getStatusCount({ commit, rootState }, { status, date }) {
    const { jwtToken } = rootState.userStore.user.signInUserSession.idToken;
    const options = {
      headers: {
        Authorization: jwtToken,
      },
    };
    try {
      commit('setLoading', true);
      const url = `${window.BASE_API.VAULT}/admin/transactions`;

      const filter = [
        {
          term: {
            type: 'payin',
          },
        },
        {
          term: {
            status,
          },
        },
      ];

      if (date) {
        filter.push({
          range: {
            timestamp: {
              gte: date.valueOf(),
            },
          },
        });
      }

      const query = {
        query: {
          bool: {
            filter,
          },
        },
      };
      const response = await axios.post(url, query, options);
      commit('setStatusCount', { status, value: _.get(response, 'data.body.hits.total.value') });
    } catch (err) {
      console.error(err);
    }
    commit('setLoading', false);
  },

  setVaultTransactionsPagination({ commit, dispatch }, pagination) {
    commit('setVaultTransactionsPagination', pagination);
  },
};

const serializeTransactions = (transactions) => {
  if (!_.has(transactions, 'data.body.hits.hits')) {
    console.error('wrong fetching transactions');

    return [];
  }

  return _.get(transactions, 'data.body.hits.hits').map((transaction) => {
    transaction = transaction._source;

    return transaction;
  });
};

const vaultTransactionsStore = {
  state,
  mutations,
  getters,
  actions,
};

export default vaultTransactionsStore;



// WEBPACK FOOTER //
// ./src/store/vault/transactionsStore.js